/**
 * Devuelve la URL normalizada si es una URL HTTPS válida; null en cualquier otro caso
 * (vacía, con espacios, sin esquema, otro protocolo, o con credenciales embebidas).
 */
export function toValidHttpsUrl(value: unknown): string | null {
  if (typeof value !== 'string' || value.length === 0 || /\s/.test(value)) {
    return null;
  }

  let url: URL;
  try {
    url = new URL(value);
  } catch {
    return null;
  }

  if (url.protocol !== 'https:' || !url.hostname || url.username || url.password) {
    return null;
  }

  return url.href;
}

/**
 * Versión del aviso de uno a tres segmentos numéricos (1, 1.1, 1.1.2), sin sufijos. Se devuelve normalizada
 * como 'mayor.menor.parche' (1, 1.0 y 1.0.0 dan '1.0.0'); null si no es válida o es 0.0.0.
 * Para ordenar versiones usar siempre compareBannerVersions, nunca comparar los strings.
 */
export function parseBannerVersion(value: unknown): string | null {
  const text = typeof value === 'number' ? String(value) : typeof value === 'string' ? value.trim() : '';
  if (!/^\d{1,9}(\.\d{1,9}){0,2}$/.test(text)) {
    return null;
  }

  const [major, minor, patch] = text.split('.').map(Number);
  if (!major && !minor && !patch) {
    return null;
  }

  return `${major}.${minor ?? 0}.${patch ?? 0}`;
}

/** Compara dos versiones ya normalizadas por segmento numérico: negativo si a < b, 0 si son iguales, positivo si a > b. */
export function compareBannerVersions(a: string, b: string): number {
  const left = a.split('.').map(Number);
  const right = b.split('.').map(Number);
  for (let i = 0; i < 3; i++) {
    if (left[i] !== right[i]) {
      return left[i] - right[i];
    }
  }

  return 0;
}

/**
 * Estado de visualización de la sesión autenticada actual (no es el estado persistido en AUTH).
 * Vive en sessionStorage: sobrevive a recargas y navegación, y se descarta al iniciar o cerrar sesión.
 */
export interface BannerSessionState {
  /** Versión normalizada. Un estado guardado por versiones anteriores del front (número) no coincide y se descarta. */
  version: string;
  /** El banner debe verse en esta sesión (false una vez cerrado con la X). */
  visible: boolean;
  /** Se hizo clic en Descargar en esta sesión: la X ya no debe registrar CLOSED. */
  downloaded: boolean;
  /** AUTH confirmó el PUT de DOWNLOADED. */
  downloadPersisted: boolean;
}

export const HU_SIGNER_PRO_BANNER_SESSION_KEY = 'huSignerProBannerSession';

/** Estado de sesión del banner para esa versión; null si no existe o no coincide. */
export function readBannerSession(version: string): BannerSessionState | null {
  try {
    const raw = sessionStorage.getItem(HU_SIGNER_PRO_BANNER_SESSION_KEY);
    const state = raw ? JSON.parse(raw) as BannerSessionState : null;
    return state && state.version === version ? state : null;
  } catch {
    return null;
  }
}

/** Guarda el estado de sesión. Falla en silencio si sessionStorage no está disponible. */
export function writeBannerSession(state: BannerSessionState): void {
  try {
    sessionStorage.setItem(HU_SIGNER_PRO_BANNER_SESSION_KEY, JSON.stringify(state));
  } catch {
    // sin sessionStorage el cierre vale solo para esta vista
  }
}

export function clearBannerSession(): void {
  try {
    sessionStorage.removeItem(HU_SIGNER_PRO_BANNER_SESSION_KEY);
  } catch {
    // nada que limpiar
  }
}
